import { create } from 'zustand';
import type { DebugLog, ToastState, ToastType, ValueUpdater } from './seedanceTypes';
import { resolveValueUpdater } from './seedanceStoreUtils';

/**
 * Seedance 全局 UI 壳状态。
 *
 * 这里放 Layout/多个页面都会触发或展示的 UI 状态，例如欢迎弹窗、
 * 配置弹窗、Toast、视频预览弹窗和调试日志。
 *
 * 不要把页面内部控件状态放到这里，例如素材组下拉开关、任务分页、
 * 拖拽 hover、提示词 @ 候选浮层等；这些应随各页面模块一起维护。
 */
interface SeedanceUiState {
  /** 首次进入或重新配置 Key 时展示的欢迎弹窗。 */
  welcomeVisible: boolean;
  /** 欢迎弹窗中的临时 Key 输入值。 */
  welcomeKey: string;
  /** 系统 token / 用户 ID 配置弹窗。 */
  configVisible: boolean;
  /** 全局 Toast，由 Layout 统一渲染。 */
  toast: ToastState | null;
  /** 当前播放/预览的视频地址。 */
  modalVideoUrl: string | null;
  /** 当前大图预览的图片地址（与生成图像页灯箱一致）。 */
  modalImageSrc: string | null;
  /** 当前弹窗播放的音频地址。 */
  modalAudioUrl: string | null;
  modalAudioTitle: string | null;
  /** API 调试日志，只保留最近一段，避免页面长期运行后无限增长。 */
  debugLogs: DebugLog[];
  setWelcomeVisible: (welcomeVisible: boolean) => void;
  setWelcomeKey: (welcomeKey: string) => void;
  setConfigVisible: (configVisible: boolean) => void;
  setToast: (toast: ToastState | null) => void;
  setModalVideoUrl: (modalVideoUrl: string | null) => void;
  setModalImageSrc: (modalImageSrc: string | null) => void;
  setModalAudioUrl: (modalAudioUrl: string | null) => void;
  setModalAudioTitle: (modalAudioTitle: string | null) => void;
  setDebugLogs: (debugLogs: ValueUpdater<DebugLog[]>) => void;
  /** 统一追加调试日志，默认最多保留最近 51 条。 */
  addDebug: (msg: string, isError?: boolean) => void;
  /** 触发全局 Toast 提示，带自动隐藏逻辑。 */
  showToast: (msg: string, type?: ToastType) => void;
  /** @internal 内部使用的定时器句柄，避免并发冲突。 */
  toastTimer: number | null;
}

export const useSeedanceUiStore = create<SeedanceUiState>((set) => ({
  welcomeVisible: false,
  welcomeKey: '',
  configVisible: false,
  toast: null,
  modalVideoUrl: null,
  modalImageSrc: null,
  modalAudioUrl: null,
  modalAudioTitle: null,
  debugLogs: [],
  setWelcomeVisible: (welcomeVisible) => set({ welcomeVisible }),
  setWelcomeKey: (welcomeKey) => set({ welcomeKey }),
  setConfigVisible: (configVisible) => set({ configVisible }),
  setToast: (toast) => set({ toast }),
  setModalVideoUrl: (modalVideoUrl) => set({ modalVideoUrl }),
  setModalImageSrc: (modalImageSrc) => set({ modalImageSrc }),
  setModalAudioUrl: (modalAudioUrl) => set({ modalAudioUrl }),
  setModalAudioTitle: (modalAudioTitle) => set({ modalAudioTitle }),
  setDebugLogs: (debugLogs) =>
    set((state) => ({ debugLogs: resolveValueUpdater(state.debugLogs, debugLogs) })),
  addDebug: (msg, isError = false) =>
    set((state) => ({
      debugLogs: [
        ...state.debugLogs.slice(-50),
        { msg, isError, time: new Date().toLocaleTimeString() },
      ],
    })),
  toastTimer: null,
  showToast: (msg, type = 's') => {
    const { toastTimer } = useSeedanceUiStore.getState();
    if (toastTimer) window.clearTimeout(toastTimer);
    const timer = window.setTimeout(() => {
      useSeedanceUiStore.setState({ toast: null, toastTimer: null });
    }, 2500);
    set({ toast: { msg, type }, toastTimer: timer });
  },
}));
